<template>
  <v-dialog :value="dialog" @input="dialog = $event" max-width="600" persistent>
    <v-card>
      <v-card-title>
        <span class="headline">Print QR Codes</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-row>
            <!-- Selected Packages Summary -->
            <v-col cols="12">
              <v-alert type="info" dense outlined>
                <strong>{{ packages.length }}</strong> package type(s) selected for printing
              </v-alert>
            </v-col>

            <!-- Print Options -->
            <v-col cols="12">
              <v-text-field
                :value="printOptions.quantity"
                @input="printOptions.quantity = Number($event)"
                label="Copies per Package Type"
                type="number"
                min="1"
                max="100"
                outlined
                dense
                :rules="[rules.required, rules.validQuantity]"
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                :value="printOptions.layout"
                @input="printOptions.layout = $event"
                :items="layoutOptions"
                label="Page Layout"
                outlined
                dense
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-select
                :value="printOptions.printerId"
                @input="printOptions.printerId = $event"
                :items="availablePrinters"
                item-text="name"
                item-value="printerId"
                label="Printer"
                outlined
                dense
                clearable
              >
                <template v-slot:prepend-item>
                  <v-list-item @click="refreshPrinters">
                    <v-list-item-icon>
                      <v-icon>mdi-refresh</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title>Refresh Printers</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-2"></v-divider>
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle v-if="item.type === 'local'">
                      Local printer via UHB
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-else-if="item.type === 'cloud'">
                      Cloud printer
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
                <template v-slot:no-data>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>No printers available</v-list-item-title>
                      <v-list-item-subtitle>
                        Install the Universal Hardware Bridge for local printers
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12">
              <v-switch
                :value="printOptions.includeLabels"
                @change="printOptions.includeLabels = $event"
                label="Include package names below QR codes"
                color="primary"
              />
            </v-col>

            <!-- Preview Section -->
            <v-col cols="12">
              <v-expansion-panels flat>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span>Preview Print Layout</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="print-preview">
                      <div class="preview-info text-center mb-3">
                        <v-chip small outlined class="mr-2">
                          Layout: {{ getLayoutDescription() }}
                        </v-chip>
                        <v-chip small outlined>
                          Total QR Codes: {{ totalQRCodes }}
                        </v-chip>
                        <v-chip small outlined class="ml-2">
                          Estimated Pages: {{ estimatedPages }}
                        </v-chip>
                      </div>
                      
                      <!-- Visual Preview Grid -->
                      <div :class="getPreviewGridClass()">
                        <div 
                          v-for="i in getPreviewCount()" 
                          :key="i"
                          class="preview-item"
                        >
                          <div class="preview-qr">
                            <v-icon>mdi-qrcode</v-icon>
                          </div>
                          <div v-if="printOptions.includeLabels" class="preview-label">
                            Package {{ i }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>

            <!-- Selected Packages List -->
            <v-col cols="12">
              <v-expansion-panels flat>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span>Selected Packages ({{ packages.length }})</span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th>Package Name</th>
                          <th>Dimensions</th>
                          <th>Copies</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="pkg in packages" :key="pkg.packageTypeId">
                          <td>{{ pkg.name }}</td>
                          <td>{{ pkg.length }}" × {{ pkg.width }}" × {{ pkg.height }}"</td>
                          <td>{{ printOptions.quantity }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="printing"
          :disabled="!isValid"
          @click="print"
        >
          <v-icon left>mdi-printer</v-icon>
          Print {{ totalQRCodes }} QR Codes
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import { PackageTypeService } from '@/services/PackageTypeService'
import { getHardwareService } from '@/services/HardwareIntegrationService'
import { PDFGenerationService } from '@/services/PDFGenerationService'

export default Vue.extend({
  name: 'BulkPrintDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    packages: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      printing: false,
      availablePrinters: [],
      localPrinters: [],
      hardwareService: null,
      packageService: null,
      pdfService: null,
      printOptions: {
        quantity: 1,
        layout: 'Grid_3x3',
        includeLabels: true,
        printerId: null
      },
      layoutOptions: [
        { text: 'Single (4" × 6")', value: 'Single_4x6' },
        { text: '2×2 Grid', value: 'Grid_2x2' },
        { text: '3×3 Grid', value: 'Grid_3x3' },
        { text: 'Avery 5160 (30 per page)', value: 'Avery_5160' },
        { text: 'Avery 5163 (10 per page)', value: 'Avery_5163' },
        { text: 'Full Page', value: 'FullPage' }
      ],
      rules: {
        required: v => !!v || 'Required',
        validQuantity: v => {
          if (!v || v < 1) return 'Must be at least 1'
          if (v > 100) return 'Maximum 100 copies'
          return true
        }
      }
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    totalQRCodes() {
      return this.packages.length * this.printOptions.quantity
    },
    estimatedPages() {
      const codesPerPage = this.getCodesPerPage()
      return Math.ceil(this.totalQRCodes / codesPerPage)
    },
    isValid() {
      return this.packages.length > 0 && 
             this.printOptions.quantity >= 1 && 
             this.printOptions.quantity <= 100
    }
  },
  created() {
    this.packageService = new PackageTypeService()
    this.hardwareService = getHardwareService()
    this.pdfService = new PDFGenerationService()
  },
  async mounted() {
    await this.loadPrinters()
    await this.initializeHardware()
  },
  methods: {
    getCodesPerPage() {
      switch (this.printOptions.layout) {
        case 'Single_4x6': return 1
        case 'Grid_2x2': return 4
        case 'Grid_3x3': return 9
        case 'Avery_5160': return 30
        case 'Avery_5163': return 10
        case 'FullPage': return 1
        default: return 9
      }
    },
    getLayoutDescription() {
      const option = this.layoutOptions.find(l => l.value === this.printOptions.layout)
      return option ? option.text : 'Unknown'
    },
    getPreviewGridClass() {
      switch (this.printOptions.layout) {
        case 'Grid_2x2': return 'preview-grid grid-2x2'
        case 'Grid_3x3': return 'preview-grid grid-3x3'
        case 'Avery_5160': return 'preview-grid grid-avery-5160'
        case 'Avery_5163': return 'preview-grid grid-avery-5163'
        default: return 'preview-grid grid-single'
      }
    },
    getPreviewCount() {
      const max = this.getCodesPerPage()
      return Math.min(max, this.totalQRCodes)
    },
    async loadPrinters() {
      const printers = []
      
      // Load cloud printers from the API
      try {
        const response = await this.$http.get('/api/print/printers')
        const printersData = response.data.printers || response.data
        const cloudPrinters = printersData
          .filter(p => p.isActive !== false)
          .map(p => ({
            ...p,
            name: `☁️ ${p.name}`,
            type: 'cloud',
            printerId: `cloud:${p.printerId}`
          }))
        printers.push(...cloudPrinters)
      } catch (error) {
        console.error('Failed to load cloud printers:', error)
        
        // Handle 431 error specifically
        if (error.response && error.response.status === 431) {
          console.error('Request headers too large (431). This usually means cookies are too large.')
          this.$emit('show-snackbar', {
            text: 'Request headers too large. Please clear your browser cookies and refresh.',
            color: 'error'
          })
        }
      }
      
      // Add local printers from UHB
      if (this.localPrinters.length > 0) {
        const uhbPrinters = this.localPrinters.map(p => ({
          ...p,
          name: `🖨️ ${p.name}`,
          type: 'local',
          printerId: `local:${p.id}`
        }))
        printers.push(...uhbPrinters)
      }
      
      // Add separator if we have both types
      if (printers.some(p => p.type === 'cloud') && printers.some(p => p.type === 'local')) {
        // Sort: local printers first, then cloud printers
        printers.sort((a, b) => {
          if (a.type === 'local' && b.type === 'cloud') return -1
          if (a.type === 'cloud' && b.type === 'local') return 1
          return 0
        })
      }
      
      this.availablePrinters = printers
    },
    
    async initializeHardware() {
      try {
        // Check if hardware service is available
        const availability = await this.hardwareService.checkAvailability()
        
        if (availability.isAvailable) {
          // Initialize and discover printers
          await this.hardwareService.initialize()
          const printers = await this.hardwareService.discoverPrinters()
          this.localPrinters = printers
          
          // Reload printer list to include local printers
          await this.loadPrinters()
        }
      } catch (error) {
        console.log('Hardware service not available:', error)
        // Not an error - just means no local printers available
      }
    },
    async refreshPrinters() {
      // Refresh local printers
      await this.initializeHardware()
      // Refresh all printers
      await this.loadPrinters()
      this.$emit('show-snackbar', {
        text: 'Printers refreshed',
        color: 'success'
      })
    },
    cancel() {
      this.dialog = false
      this.printOptions = {
        quantity: 1,
        layout: 'Grid_3x3',
        includeLabels: true,
        printerId: null
      }
    },
    async print() {
      if (!this.isValid) return

      this.printing = true
      try {
        // Determine printer type and ID
        let actualPrinterId = null
        let isLocalPrinter = false

        if (this.printOptions.printerId) {
          if (this.printOptions.printerId.startsWith('local:')) {
            isLocalPrinter = true
            actualPrinterId = this.printOptions.printerId.substring(6)
          } else if (this.printOptions.printerId.startsWith('cloud:')) {
            actualPrinterId = this.printOptions.printerId.substring(6)
          } else {
            actualPrinterId = this.printOptions.printerId
          }
        }

        // Convert packages to the format expected by PDFGenerationService
        const packagesForPrint = this.packages.map(pkg => ({
          packageTypeId: pkg.packageTypeId,
          name: pkg.name,
          length: pkg.length,
          width: pkg.width,
          height: pkg.height,
          weight: pkg.weight
        }))

        const printOptions = {
          layout: this.printOptions.layout,
          quantity: this.printOptions.quantity,
          includeLabels: this.printOptions.includeLabels
        }

        // Generate PDF client-side
        this.$emit('show-snackbar', {
          text: 'Generating QR codes...',
          color: 'info'
        })

        const pdfDataUri = await this.pdfService.generateQRCodesAndPDF(packagesForPrint, printOptions)
        const totalCodes = packagesForPrint.length * this.printOptions.quantity

        this.$emit('show-snackbar', {
          text: `Successfully generated ${totalCodes} QR codes`,
          color: 'success'
        })

        // Log the usage for analytics (optional server call)
        try {
          await this.packageService.bulkPrintQRCodes({
            packageTypeIds: this.packages.map(p => p.packageTypeId),
            quantity: this.printOptions.quantity,
            layout: this.printOptions.layout,
            includeLabels: this.printOptions.includeLabels,
            printerId: actualPrinterId
          })
        } catch (error) {
          console.warn('Failed to log print usage:', error)
        }

        // Handle printing/downloading based on printer type
        if (isLocalPrinter && actualPrinterId) {
          // For local printing via UHB, use the PDF data URI directly
          try {
            await this.hardwareService.connectToPrinter(actualPrinterId)

            // Validate the PDF data URI format
            if (!pdfDataUri.startsWith('data:')) {
              throw new Error('Invalid PDF data format - must be a data URI')
            }

            // UHB printLabelFromUrl expects HTTP/HTTPS/DATA URLs
            // Pass the data URI directly since it's already a valid DATA URL
            const printResult = await this.hardwareService.printLabelFromUrl(actualPrinterId, pdfDataUri)

            if (printResult.success) {
              this.$emit('show-snackbar', {
                text: 'QR codes sent to local printer',
                color: 'success'
              })
            } else {
              throw new Error(printResult.error || 'Local printing failed')
            }
          } catch (error) {
            console.error('Failed to print to local printer:', error)
            this.$emit('show-snackbar', {
              text: `Local printing failed: ${error.message}. PDF downloaded instead.`,
              color: 'warning'
            })
            // Fallback to download with descriptive filename
            const filename = this.generateFilename()
            await this.pdfService.downloadPDF(pdfDataUri, filename)
          }
        } else if (actualPrinterId) {
          // Cloud printer - open PDF in new tab for printing
          await this.pdfService.printPDF(pdfDataUri)
        } else {
          // No printer selected - download PDF with descriptive filename
          const filename = this.generateFilename()
          await this.pdfService.downloadPDF(pdfDataUri, filename)
        }

        this.$emit('printed')
        this.cancel()
      } catch (error) {
        console.error('Failed to generate QR codes:', error)
        this.$emit('show-snackbar', {
          text: `Failed to generate QR codes: ${error.message}`,
          color: 'error'
        })
      } finally {
        this.printing = false
      }
    },
    generateFilename() {
      const date = new Date()
      const dateStr = date.toISOString().split('T')[0] // YYYY-MM-DD
      const timeStr = date.toTimeString().split(' ')[0].replace(/:/g, '-') // HH-MM-SS

      // Create descriptive filename components
      const layoutName = this.printOptions.layout.replace('_', '-').toLowerCase()
      const packageCount = this.packages.length
      const quantity = this.printOptions.quantity
      const totalCodes = packageCount * quantity

      // Fixed components that we always want
      const prefix = 'qr-codes'
      const suffix = `.pdf`
      const timestamp = `${dateStr}-${timeStr}` // 19 chars: YYYY-MM-DD-HH-MM-SS
      const layout = layoutName // max ~12 chars
      const codes = `${totalCodes}codes` // max ~8 chars

      // Calculate available space for package info
      // Target max 150 chars total to be very safe across all filesystems
      const fixedLength = prefix.length + suffix.length + timestamp.length + layout.length + codes.length + 6 // 6 for dashes
      const availableForPackages = Math.max(0, 150 - fixedLength)

      // Build package info within available space
      let packageInfo = ''
      if (packageCount === 1) {
        packageInfo = this.sanitizeForFilename(this.packages[0].name, availableForPackages)
      } else if (packageCount <= 3 && availableForPackages > 15) {
        // Only include names if we have reasonable space
        const spacePerPackage = Math.floor(availableForPackages / packageCount) - 1 // -1 for separator
        packageInfo = this.packages
          .map(p => this.sanitizeForFilename(p.name, spacePerPackage))
          .join('-')
          .substring(0, availableForPackages)
      } else {
        packageInfo = `${packageCount}pkg`
      }

      // Build final filename
      const parts = [prefix, packageInfo, layout, codes, timestamp]
      const filename = parts.filter(p => p.length > 0).join('-') + suffix

      // Final safety check - should never exceed 150 chars now
      return filename.length > 150 ? filename.substring(0, 147) + suffix : filename
    },

    sanitizeForFilename(name, maxLength) {
      return name
        .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special chars but keep spaces
        .replace(/\s+/g, '-') // Replace spaces with dashes
        .replace(/-+/g, '-') // Replace multiple dashes with single dash
        .replace(/^-+|-+$/g, '') // Remove leading/trailing dashes
        .substring(0, maxLength)
        .toLowerCase()
    }
  }
})
</script>

<style scoped>
.print-preview {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
}

.preview-grid {
  display: grid;
  gap: 10px;
  margin-top: 10px;
}

.grid-single {
  grid-template-columns: 1fr;
  max-width: 200px;
  margin: 0 auto;
}

.grid-2x2 {
  grid-template-columns: repeat(2, 1fr);
}

.grid-3x3 {
  grid-template-columns: repeat(3, 1fr);
}

.grid-2x5 {
  grid-template-columns: repeat(2, 1fr);
}

.grid-avery-5160 {
  grid-template-columns: repeat(3, 1fr);
  gap: 3px;
  max-height: 400px;
  overflow-y: auto;
}

.grid-avery-5163 {
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
}

.preview-item {
  text-align: center;
  background: white;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.preview-qr {
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  margin-bottom: 5px;
}

.preview-label {
  font-size: 10px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>